import { useState, useEffect, useRef } from 'react';
import './styles.scss';
import { useTranslation } from 'react-i18next';
import { useSmartField } from 'hooks/use-smart-field';
import { Select } from 'components/common/select';
import { FormControl, MenuItem } from '@mui/material';
import { InputLabel } from 'components/common/input-label';
import { Instalment } from 'features/payment/interfaces';
import { TextField } from 'components/common/text-field';

export interface Bin {
  brand: string;
  bin: string;
  type: string;
  category?: string;
  country?: string;
  issuer?: string;
}

export interface DLocalInstance {
  getBinInformation: (cardInstance: any) => Bin;
  createToken: (cardInstance: any, { name }: { name: string }) => Promise<{ token: string }>;
  createInstallmentsPlan: (field: any, amount: string, currency: string) => Promise<{}>;
  fields: ({}) => any;
}

export interface SmartFieldError {
  pan?: string;
  expiration?: string;
  cvv?: string;
}

interface SmartFieldProps {
  locale: string;
  disabled: boolean;
  country: string;
  currency: string;
  totalAmount: string;
  installmentsId: string;
  error: SmartFieldError;
  installmentsDisabled?: boolean;
  onChangeError: (error: SmartFieldError) => void;
  onChangeBrand: (brand: string) => void;
  onChangeInstallments: (installment: Instalment | null) => void;
  onChangeSmartFieldData: ({ dlocal, card }: { dlocal?: DLocalInstance; card: any }) => void;
}

const SmartField = ({
  locale,
  disabled = false,
  country,
  currency,
  totalAmount,
  installmentsId,
  error,
  installmentsDisabled = false,
  onChangeError,
  onChangeBrand,
  onChangeInstallments,
  onChangeSmartFieldData,
}: SmartFieldProps) => {
  const smartFieldRef = useRef(null);
  const { t } = useTranslation();
  const [installmentsOptions, setInstallmentsOptions] = useState<Instalment[]>([]);

  const [loadSmartFields, setLoadSmartFields] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      onChangeInstallments(null);
      const { dlocal, card } = useSmartField({
        translation: t,
        locale,
        country,
        currency,
        totalAmount,
        onChangeError: (err: SmartFieldError) => onChangeError({ ...error, ...err }),
        onChangeBrand,
        onChangeInstallments,
        onChangeInstallmentsOptions: setInstallmentsOptions,
      });
      onChangeSmartFieldData({ dlocal, card });
    };
    if (loadSmartFields) fetchData();
  }, [loadSmartFields]);

  useEffect(() => {
    setLoadSmartFields(true);
  }, []);

  return (
    <div className="smart_field" ref={smartFieldRef}>
      <div className="smart_field_box smart_field_box_full">
        <label
          htmlFor="pan-field"
          className={`smart_field_label ${!!error?.pan ? 'smart_field_label--with-error' : ''} ${
            disabled ? 'smart_field_label--disabled' : ''
          }`}
        >
          {t('smartField.cardNumber')}
        </label>
        <div
          id="pan-field"
          className={`smart_field_card ${error?.pan ? 'smart_field_card_with_error' : ''}  ${
            disabled ? 'smart_field_card_disabled' : ''
          }`}
        >
          <TextField fullWidth disabled />
        </div>
        {error?.pan && (
          <div id="card-errors" className="smart_field_error" role="alert">
            {error?.pan}
          </div>
        )}
      </div>
      <div className="smart_field_box smart_field_box_left smart_field_box_left_60">
        <label
          htmlFor="expiration-field"
          className={`smart_field_label ${!!error?.expiration ? 'smart_field_label--with-error' : ''} ${
            disabled ? 'smart_field_label--disabled' : ''
          }`}
        >
          {t('smartField.expDate')}
        </label>
        <div
          id="expiration-field"
          className={`smart_field_card ${error?.expiration ? 'smart_field_card_with_error' : ''}  ${
            disabled ? 'smart_field_card_disabled' : ''
          }`}
        >
          <TextField fullWidth disabled />
        </div>
        {error?.expiration && (
          <div id="card-errors" className="smart_field_error" role="alert">
            {error?.expiration}
          </div>
        )}
      </div>
      <div className="smart_field_box smart_field_box_right smart_field_box_right_40">
        <label
          htmlFor="pan-field"
          className={`smart_field_label ${!!error?.cvv ? 'smart_field_label--with-error' : ''} ${
            disabled ? 'smart_field_label--disabled' : ''
          }`}
        >
          {t('smartField.cvv')}
        </label>
        <div
          id="cvv-field"
          className={`smart_field_card ${error?.cvv ? 'smart_field_card_with_error' : ''}  ${
            disabled ? 'smart_field_card_disabled' : ''
          }`}
        >
          <TextField fullWidth disabled />
        </div>
        {error?.cvv && (
          <div id="card-errors" className="smart_field_error" role="alert">
            {error?.cvv}
          </div>
        )}
      </div>
      {!installmentsDisabled && (
        <div className="smart_field_box smart_field_box_full">
          <FormControl fullWidth disabled={disabled || installmentsOptions?.length < 1}>
            <InputLabel>{t(`smartField.fees${country === 'MX' ? '.MX' : ''}`)}</InputLabel>
            <Select
              label={t(`smartField.fees${country === 'MX' ? '.MX' : ''}`)}
              fullWidth
              name="installments"
              value={installmentsId}
              onChange={e => onChangeInstallments(installmentsOptions.filter(op => op.value === e.target.value)[0])}
              disabled={disabled || installmentsOptions?.length < 1}
            >
              <MenuItem disabled value="">
                <em>{t(`smartField.fees${country === 'MX' ? '.MX' : ''}`)}</em>
              </MenuItem>
              {installmentsOptions?.map((op, idx) => {
                return (
                  <MenuItem key={idx} value={op.value}>
                    {op.displayName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      )}
    </div>
  );
};

export default SmartField;
